import { useCallback, useEffect, useRef, useState } from "react";
import {
  BAR_WIDTH,
  MIN_BAR_HEIGHT,
  WAVE_COLOR,
  PLAYED_COLOR,
  barsCountForWidth,
} from "./waveConfig";

const DEFAULTS = {
  barCount: 40,
  minBarHeight: MIN_BAR_HEIGHT,
  previewGamma: 0.55,
  color: WAVE_COLOR,
  playedColor: PLAYED_COLOR,
};
const downsample = (source, count) => {
  if (!source || !source.length) return [];
  const result = [];
  const chunk = source.length / count;
  for (let i = 0; i < count; i++) {
    const from = Math.floor(i * chunk);
    const to = Math.max(from + 1, Math.floor((i + 1) * chunk));
    let max = 0;
    for (let j = from; j < to; j++) {
      max = Math.max(max, source[j] || 0);
    }
    result.push(max);
  }
  return result;
};

export default function useAudioWaveform(options = {}) {
  const config = { ...DEFAULTS, ...options };
  const { barCount, minBarHeight, previewGamma, color, playedColor } = config;

  const canvasRef = useRef(null);
  const analyserRef = useRef(null);
  const freqDataRef = useRef(null);
  const rafRef = useRef(null);
  const peaksRef = useRef([]);
  const [isLive, setIsLive] = useState(false);
  const [progress, setProgress] = useState(0);

  const paint = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const cssWidth = canvas.clientWidth || 240;
    const cssHeight = canvas.clientHeight || 40;
    const pixelWidth = Math.max(1, Math.floor(cssWidth * dpr));
    const pixelHeight = Math.max(1, Math.floor(cssHeight * dpr));
    if (canvas.width !== pixelWidth || canvas.height !== pixelHeight) {
      canvas.width = pixelWidth;
      canvas.height = pixelHeight;
    }

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssWidth, cssHeight);

    const liveFrame = !!analyserRef.current && !!freqDataRef.current;
    let bars = [];

    if (liveFrame) {
      const data = freqDataRef.current;
      const step = Math.max(1, Math.floor(data.length / barCount));
      for (let i = 0; i < barCount; i++) {
        let sum = 0;
        for (let j = 0; j < step; j++) sum += data[i * step + j] || 0;
        bars.push(Math.min(1, sum / step / 255));
      }
      const average = bars.reduce((acc, value) => acc + value, 0) / bars.length;
      peaksRef.current.push(average);
      if (peaksRef.current.length > 400) peaksRef.current.shift();
    } else {
      const raw = downsample(peaksRef.current, barCount);
      const max = raw.reduce((acc, value) => Math.max(acc, value), 0);
      bars =
        max > 0.001
          ? raw.map((value) => Math.pow(value / max, previewGamma))
          : raw;
    }

    if (!bars.length) return;

    const barWidth = BAR_WIDTH;
    const count = barsCountForWidth(cssWidth);
    const step = cssWidth / count;
    const scaled = bars.length > count ? downsample(bars, count) : bars;
    const playedX = progress * cssWidth;

    scaled.forEach((value, index) => {
      const h = Math.max(minBarHeight, value * cssHeight);
      const x = index * step;
      const y = (cssHeight - h) / 2;
      ctx.fillStyle =
        !liveFrame && x + barWidth / 2 <= playedX ? playedColor : color;
      ctx.fillRect(x, y, barWidth, h);
    });
  }, [barCount, minBarHeight, previewGamma, color, playedColor, progress]);

  const stopStream = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (analyserRef.current) {
      try {
        analyserRef.current.disconnect();
      } catch (e) {}
      analyserRef.current = null;
    }
    freqDataRef.current = null;
    setIsLive(false);
  }, []);

  const startStream = useCallback(
    (recorder) => {
      const context = recorder && recorder.context;
      const source = recorder && recorder.microphone;
      if (!context || !source) return;

      if (context.state === "suspended" && context.resume) {
        context.resume();
      }

      const analyser = context.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.75;
      source.connect(analyser);

      analyserRef.current = analyser;
      freqDataRef.current = new Uint8Array(analyser.frequencyBinCount);
      setIsLive(true);

      const loop = () => {
        analyser.getByteFrequencyData(freqDataRef.current);
        paint();
        rafRef.current = requestAnimationFrame(loop);
      };
      rafRef.current = requestAnimationFrame(loop);
    },
    [paint]
  );

  const resetPeaks = useCallback(() => {
    peaksRef.current = [];
    setProgress(0);
  }, []);

  useEffect(() => {
    if (!isLive) paint();
  }, [progress, isLive, paint]);

  useEffect(
    () => () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    },
    []
  );

  return {
    canvasRef,
    peaksRef,
    isLive,
    progress,
    setProgress,
    startStream,
    stopStream,
    resetPeaks,
    repaint: paint,
  };
}
